import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import { IReportingPeriodItem } from "../../model/report/ReportingPeriodItem";
import Application from "../../Application";
import GetDataReport8Interactor from "../../interactor/report/GetDataReport8Interactor";
import Report8ApplicantPresentationModel, {
  IReport8ApplicantPresentationModel
} from "./model/Report8ApplicantPresentationModel";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetReport8ApplicantsInteractor from "../../interactor/report/GerReport8ApplicantsInteractor";
import { ReportConstants } from "../../common/Constants";

export interface TReport8Presenter extends TLoadingAwarePresenter {
  report8ReportingPeriodList: IReportingPeriodItem[];
  report8CurrentReportingPeriod?: IReportingPeriodItem;
  report8?: IReport8ApplicantPresentationModel[];
  report8ApplicantList: IReport8ApplicantPresentationModel[];
  report8SelectedApplicantIds: number[];
}

export interface IReport8Presenter extends TReport8Presenter, TPresentable {
  setReportingPeriodList(reportingPeriodList: IReportingPeriodItem[]): void;
  onApplicantsChange(selectedApplicantsId: any[]): void;
  onChangeReportingPeriod(reportingPeriodId: number): void;
  getPDFReport8(selectedApplicantIds: number[]): Promise<any>;
  onApplicantSearch(fullName: string): void;
}

const defaultState: TReport8Presenter = {
  report8ReportingPeriodList: [],
  report8ApplicantList: [],
  report8SelectedApplicantIds: []
};

const Report8Presenter = withStore<IReport8Presenter, TReport8Presenter>(
  ({ application, store, loader, translate }): IReport8Presenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TReport8Presenter>();

    const setReportingPeriodList = (
      report8ReportingPeriodList: IReportingPeriodItem[]
    ) => {
      _store.update({
        report8ReportingPeriodList
      });
    };

    const onChangeReportingPeriod = async (reportingPeriodId: number) => {
      const report8ReportingPeriodList =
        _store.getState<TReport8Presenter>().report8ReportingPeriodList;
      const report8CurrentReportingPeriod = report8ReportingPeriodList.find(
        (reportingPeriod) => reportingPeriod.id === reportingPeriodId
      );
      _store.update({
        report8CurrentReportingPeriod,
        report8: undefined
      });
    };

    const getPDFReport8 = async () => {
      const report8CurrentReportingPeriod =
        _store.getState<TReport8Presenter>().report8CurrentReportingPeriod;
      loader.start("report8Loader");
      const report8SelectedApplicantIds =
        _store.getState<TReport8Presenter>().report8SelectedApplicantIds;
      const applicantDetails =
        report8CurrentReportingPeriod &&
        (
          await application.container
            .resolve<GetDataReport8Interactor>("getDataReport8")
            .execute(
              report8CurrentReportingPeriod.id,
              report8SelectedApplicantIds
            )
        ).map(Report8ApplicantPresentationModel);
      _store.update({
        report8: applicantDetails,
        report8SelectedApplicantIds
      });
      loader.stop("report8Loader");
    };

    const onApplicantsChange = (report8SelectedApplicantIds: string[]) => {
      if (report8SelectedApplicantIds.length === 0) {
        _store.update({
          report8SelectedApplicantIds,
          report8ApplicantList: [],
          report8: undefined
        });
      } else {
        _store.update({ report8SelectedApplicantIds, report8: undefined });
      }
    };

    const onApplicantSearch = async (fullName: string) => {
      try {
        if (
          fullName.length >= ReportConstants.AUTOCOMPLETE.APPLICANT_MIN_LENGTH
        ) {
          let report8ApplicantList = await application.container
            .resolve<GetReport8ApplicantsInteractor>("getReport8Applicants")
            .execute(fullName);
          _store.update({
            report8ApplicantList: report8ApplicantList || []
          });
        } else {
          _store.update({
            report8ApplicantList: []
          });
        }
      } catch (error) {
        application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      setReportingPeriodList,
      onChangeReportingPeriod,
      getPDFReport8,
      onApplicantsChange,
      onApplicantSearch
    };
  },
  defaultState
);

export default Report8Presenter;
